import { useEffect, useState } from "react";
import { useTill } from "../../app/till-context";
import { t } from "../../i18n";
import { Dialog } from "../../ui/Dialog";
import { FloorPlan } from "./FloorPlan";

const CLOCK_MS = 30_000;

interface Props {
  /** The table on the ticket now ("" when none). */
  current: string;
  onPick: (table: string) => void;
  /** "⇄ Tafel verplaatsen": the occupied table whose guests move. */
  onMove: (from: string) => void;
  onClose: () => void;
}

/**
 * "Kies tafel" as in the prototype: the floor plan per room. A free table starts a new order there;
 * an occupied table opens its order to order more or to pay. "⇄ Tafel verplaatsen" first asks which
 * occupied table moves.
 */
export function TablePicker({ current, onPick, onMove, onClose }: Props) {
  const { floor, openTables } = useTill();
  const [roomId, setRoomId] = useState(
    () => (floor.find((room) => room.items.some((item) => item.label === current)) ?? floor[0])?.id ?? "",
  );
  const [moving, setMoving] = useState(false);
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = setInterval(() => {
      setNow(Date.now());
    }, CLOCK_MS);
    return () => {
      clearInterval(timer);
    };
  }, []);

  const room = floor.find((candidate) => candidate.id === roomId) ?? floor[0];
  const occupiedIn = (items: { kind: string; label: string | null }[]) =>
    items.filter((item) => item.kind === "table" && item.label !== null && openTables.has(item.label)).length;

  return (
    <Dialog
      title={moving ? t.tables.whichToMove : t.tables.choose}
      size="floor"
      onClose={onClose}
      actions={
        !moving && openTables.size > 0 ? (
          <button
            type="button"
            className="btn"
            onClick={() => {
              setMoving(true);
            }}
          >
            {t.tables.move}
          </button>
        ) : undefined
      }
    >
      {floor.length > 1 && (
        <div className="seg fp-rooms" role="group" aria-label="Ruimte">
          {floor.map((candidate) => {
            const busy = occupiedIn(candidate.items);
            return (
              <button
                key={candidate.id}
                type="button"
                aria-pressed={candidate.id === room?.id}
                onClick={() => {
                  setRoomId(candidate.id);
                }}
              >
                {candidate.name}
                {busy > 0 && ` · ${t.tables.busyCount(busy)}`}
              </button>
            );
          })}
        </div>
      )}
      {room && (
        <FloorPlan
          room={room}
          openTables={openTables}
          current={current}
          now={now}
          enabled={(_table, occupied) => !moving || occupied}
          onTable={(table) => {
            if (moving) onMove(table);
            else onPick(table);
          }}
        />
      )}
      <p className="note">{t.tables.note}</p>
    </Dialog>
  );
}
